/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import DeviceWrapper from '@/components/AZCustomComponent/DeviceWrapper';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import type { AddToCartData } from '@/types';
import CertonaPartsCarousel from './CertonaPartsCarousel';
import { useHeaderData } from '@/features/header/api/getHeader';
import { defaultStoreUS } from '@/constants/locale';
import { useGetProductSkuPrice } from '@/features/product/api/getProductSkuPrice';
import { useProductAvailability } from '@/features/product/api/getProductAvailability';
import { useDeviceType } from '@/utils/useDeviceType';
import { type CertonaBlockProps } from '../interface';
import { getAvailableCertonaProducts } from '@/utils/getAvailableCertonaProducts';
import { useAppState } from '@/stores/AppState';
import { isServer } from '@/utils/isServer';

export const CertonaBlock = ({
  id,
  contentItem,
  className,
  isFromExternal,
  onCertonaRecommendationClick,
  onCertonaAddToCartClick,
  updateAfterAddtoCartClick,
  certonaRecommendations,
  onlyShowAvailableProductRecommendations = false,
  skipAvailabilityFilter = true,
  containerType = '',
  displayCertonaBlock = true,
  thresholdHeaderText = '',
  subtitle = '',
  inDrawer = false,
  LoadingSkeleton,
  cardsContainerCustomClass,
  setAddedCertonaItemObject,
  certonaType,
  shelfSeoUrl,
  checkContainerNameIncludes = false,
  title,
  getCtjAnalytics,
  showAddToCartCTA,
  recTabNumber,
}: CertonaBlockProps) => {
  const isOosOrDoesNotFit = certonaType === 'OOS' || certonaType === 'DNF';
  const deviceType = useDeviceType();
  const isBot = deviceType === 'bot';
  const certonaAddToCartEnabled = !isBot && onlyShowAvailableProductRecommendations;
  const priceOnlyMode = certonaAddToCartEnabled && skipAvailabilityFilter;
  const locale = useLocale();
  const {
    state: { certonaRecommendationsLoaded },
  } = useAppState();

  const isAccessoriesPdp =
    !isServer && window.certona && window.certona.pagetype === 'accessoriesproduct';
  const index =
    (checkContainerNameIncludes && !!contentItem?.containerID
      ? certonaRecommendations?.findIndex(
          (item) =>
            item.scheme.includes(contentItem.containerID ?? '') &&
            ((isAccessoriesPdp && (contentItem.containerID ?? '').includes('accessories')) ||
              !isAccessoriesPdp),
        )
      : certonaRecommendations?.findIndex((item) => item.scheme === contentItem?.containerID)) ??
    -1;
  const items = certonaRecommendations?.[index]?.items;
  const skuNumbers = items?.map((part) => part.id);

  const addToCartAnalyticsandSuccessMessage = (analyticsData: AddToCartData, blockId?: string) => {
    onCertonaAddToCartClick?.(analyticsData, blockId);
  };
  const {
    data: productAvailability,
    isFetching: productAvailabilityIsFetching,
    isSuccess: productAvailabilityIsSuccess,
  } = useProductAvailability({
    skuIds: skuNumbers ?? null,
    enabled:
      !!skuNumbers?.length &&
      certonaAddToCartEnabled &&
      displayCertonaBlock &&
      !skipAvailabilityFilter,
  });

  const { data: headerData } = useHeaderData();
  const storeNumber = headerData?.storeNumber ?? defaultStoreUS;
  const { data: pricingInfo, isFetching: pricingIsFetching } = useGetProductSkuPrice({
    skuNumbers,
    storeNumber,
    enabled: !!storeNumber && !!skuNumbers?.length,
    staleTime: 5 * 60000, // 5 minutes
  });

  const skuDetailsData =
    !priceOnlyMode && productAvailabilityIsSuccess && certonaAddToCartEnabled
      ? productAvailability
      : null;

  const availableProducts = getAvailableCertonaProducts({
    items,
    skuDetailsData,
    pricingInfo,
    thresholdHeaderText,
    filterByPriceOnly: skipAvailabilityFilter,
  });

  const showCertonaBlock = certonaAddToCartEnabled
    ? priceOnlyMode
      ? (availableProducts?.length ?? 0) > 0 && displayCertonaBlock
      : productAvailabilityIsSuccess && (availableProducts?.length ?? 0) > 0 && displayCertonaBlock
    : displayCertonaBlock;

  const recommendationsData = certonaAddToCartEnabled
    ? availableProducts
    : certonaRecommendations?.[index]?.items;

  const showSkeleton = certonaAddToCartEnabled
    ? priceOnlyMode
      ? (!certonaRecommendationsLoaded || pricingIsFetching) && displayCertonaBlock
      : productAvailabilityIsFetching && displayCertonaBlock
    : !certonaRecommendationsLoaded && displayCertonaBlock;

  if ((locale === countryCodes.mx && certonaType !== 'CTJ') || locale === countryCodes.ptBr) {
    return null;
  }

  return certonaRecommendations?.[index]?.display &&
    certonaRecommendations[index]?.items.length > 0 &&
    showCertonaBlock ? (
    <DeviceWrapper
      desktop={contentItem?.desktop || false}
      tablet={contentItem?.tablet || false}
      mobile={contentItem?.mobile || false}
    >
      <div className={className} data-testid="certona-carousel-container">
        {title || null}
        <CertonaPartsCarousel
          id={id}
          title={
            !contentItem?.name || isOosOrDoesNotFit || id === 'certona-threshold'
              ? ''
              : certonaRecommendations[index].explanation
          }
          subtitle={subtitle}
          // @ts-expect-error fix type
          partsList={recommendationsData}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          pageType={isOosOrDoesNotFit ? 'pencilpdp' : window?.certona?.pagetype}
          greyBackground={contentItem?.themeCode?.toLowerCase() === 'grey' || isFromExternal}
          isFromExternal={isFromExternal}
          onCertonaRecommendationClick={onCertonaRecommendationClick}
          addToCartAnalyticsandSuccessMessage={(analyticsData: AddToCartData) =>
            addToCartAnalyticsandSuccessMessage(analyticsData, id)
          }
          updateAfterAddtoCartClick={updateAfterAddtoCartClick}
          skuDetailsData={skuDetailsData}
          containerID={containerType}
          pricingInfo={pricingInfo}
          thresholdHeaderText={thresholdHeaderText}
          inDrawer={inDrawer}
          cardsContainerCustomClass={cardsContainerCustomClass}
          setAddedCertonaItemObject={setAddedCertonaItemObject}
          certonaType={certonaType}
          shelfSeoUrl={shelfSeoUrl}
          getCtjAnalytics={getCtjAnalytics}
          showAddToCartCTA={showAddToCartCTA}
          recTabNumber={recTabNumber}
        />
      </div>
    </DeviceWrapper>
  ) : LoadingSkeleton && showSkeleton ? (
    <div className={className}>
      <LoadingSkeleton />
    </div>
  ) : null;
};
